import { isAsyncPace, type GameConfig } from '../../shared/config';
import type { SessionArchivedResult } from '../session/room-session-state';
import {
  hasRoomContext,
  home,
  identifyRoomNavigation,
  restorableNavigation,
  transition,
  visibleNavigation,
  type Navigation,
  type NavigationEvent,
} from './navigation';
import { BrowserNavigationHistory, navigationPath } from './url-sync.svelte';

interface NavigationSession {
  holdsRoom: boolean;
  roomCode: string;
  archivedResult: Pick<SessionArchivedResult, 'roomCode' | 'roundId'> | null;
  activeConfig: Readonly<GameConfig> | null;
  config: Readonly<GameConfig>;
}

interface NavigationWorkflowHooks {
  session(): NavigationSession;
  confirmLeave(): Promise<boolean>;
  leave(): void;
  detach(): void;
  clearSelection(): void;
  refreshRooms(): void;
  cancelAutoStart(): void;
  clearJoinStatus(): void;
}

/** How a room is released: `leave` ends membership, `detach` keeps it (and the reconnect token). */
type RoomRelease = 'leave' | 'detach';

/** Coordinates app navigation, History API commits, and the session effects of leaving a room. */
export class NavigationWorkflow {
  current = $state<Navigation>(home());
  private history: BrowserNavigationHistory | undefined;

  constructor(
    initial: Navigation,
    private readonly hooks: NavigationWorkflowHooks,
  ) {
    this.current = initial;
  }

  /** The mounted URL is kept as-is: a room invitation stays reloadable while the welcome dialog waits. */
  start(): void {
    this.history = new BrowserNavigationHistory(this.current, undefined, this.applyHistory);
  }

  navigate(event: NavigationEvent, history: 'push' | 'replace' = 'push'): void {
    const target = transition(this.current, event);
    if (target === this.current) return;
    this.current = target;
    this.history?.[history](target, this.path(target));
  }

  replaceUrl(archiveRoundId?: string | null, roomCode?: string): void {
    if (roomCode) this.current = identifyRoomNavigation(this.current, roomCode);
    this.history?.replace(this.current, this.path(this.current, archiveRoundId, roomCode));
  }

  async backTo(target: Navigation): Promise<void> {
    if (this.history?.backTo(target)) return;
    await this.applyHistory(
      target,
      () => true,
      (navigation, path) => this.history?.replace(navigation, path),
    );
  }

  /**
   * Release the room and go Home. `by-pace` detaches from turn-taking rooms, which stay on the
   * menu, and leaves live ones.
   */
  exitHome(release: RoomRelease | 'by-pace'): void {
    this.releaseRoom(release === 'by-pace' ? this.paceRelease() : release);
    this.navigate({ type: 'home' }, 'replace');
  }

  dispose(): void {
    this.history?.dispose();
    this.history = undefined;
  }

  private readonly applyHistory = async (
    requested: Navigation,
    isCurrent: () => boolean,
    commit: (navigation: Navigation, path: string) => void,
  ): Promise<boolean> => {
    const before = this.hooks.session();
    const currentRoom = visibleNavigation(this.current);
    // A setup can be shown beneath Settings before the server has assigned its room code.
    const activeRoomCode = before.archivedResult
      ? before.archivedResult.roomCode
      : before.holdsRoom
        ? currentRoom.kind === 'room'
          ? (currentRoom.roomCode ?? '')
          : before.roomCode
        : null;
    const target = restorableNavigation(requested, activeRoomCode);
    const leavingRoom = hasRoomContext(this.current) && !hasRoomContext(target);
    if (leavingRoom && !(await this.hooks.confirmLeave())) return false;
    if (!isCurrent()) return false;

    // A shown archive is room state too: keeping it would present it again on the next room entry.
    if (leavingRoom && before.holdsRoom) this.releaseRoom(this.paceRelease());
    // Modal dismissal preserves pending setup intent; failed joins retain their error on the form.
    this.current = target;
    commit(target, this.path(target));
    return true;
  };

  private paceRelease(): RoomRelease {
    const session = this.hooks.session();
    return isAsyncPace(session.activeConfig ?? session.config) ? 'detach' : 'leave';
  }

  private releaseRoom(release: RoomRelease): void {
    if (release === 'detach') this.hooks.detach();
    else this.hooks.leave();
    this.hooks.clearSelection();
    this.hooks.refreshRooms();
    this.hooks.cancelAutoStart();
    this.hooks.clearJoinStatus();
  }

  private path(
    navigation: Navigation,
    archiveRoundId = this.hooks.session().archivedResult?.roundId ?? null,
    roomCode?: string,
  ): string {
    const visible = visibleNavigation(navigation);
    const identifiedCode = visible.kind === 'room' ? (roomCode ?? visible.roomCode ?? '') : '';
    return navigationPath(navigation, { roomCode: identifiedCode, archivedRoundId: archiveRoundId });
  }
}
